import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Dialog, Menu, Transition } from "@headlessui/react";
import {
  WrenchScrewdriverIcon,
  PlusCircleIcon,
  BoltIcon,
  CheckCircleIcon,
  MegaphoneIcon,
  EyeSlashIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  confirmUpdateComplete,
  createPlatformNotice,
  createScheduledUpdate,
  fetchPlatformNoticesAdmin,
  fetchScheduledUpdatesHistory,
  startMaintenance,
  updatePlatformNotice,
  type NoticePayload,
  type ScheduledUpdatePayload,
} from "../../api/platformBroadcasts";
import { TWclassNames } from "../utils/Div";
import { usePlatformBroadcast } from "./PlatformBroadcastContext";
import type { RootState } from "../../store";
import {
  ROLE_VIEW_PRESET_ORDER,
  type RoleViewPresetId,
} from "../../contexts/roleViewOverridePresets";
import { sessionRoleRowsToActiveFlatNames } from "../../contexts/roleViewFlatAdapter";
import { useRoleViewOverride } from "../../contexts/RoleViewOverrideContext";
import { UserRoles } from "../dashboard/company/users/user-roles";

type RoleViewDialogTab = "presets" | "custom";

function localDatetimeValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

export function DevOpsFloatingMenu() {
  const { t } = useTranslation();
  const broadcast = usePlatformBroadcast();
  const state = broadcast?.state ?? null;
  const refresh = broadcast?.refresh ?? (async () => {});

  const [dlgSchedule, setDlgSchedule] = useState(false);
  const [dlgMaintain, setDlgMaintain] = useState(false);
  const [dlgNotice, setDlgNotice] = useState(false);
  const [dlgManageNotices, setDlgManageNotices] = useState(false);
  const [dlgRoleView, setDlgRoleView] = useState(false);
  const [draftRolePreset, setDraftRolePreset] =
    useState<RoleViewPresetId>("off");
  const [roleViewTab, setRoleViewTab] =
    useState<RoleViewDialogTab>("presets");
  const [customPanelKey, setCustomPanelKey] = useState(0);

  const roleOverride = useRoleViewOverride();
  const reduxUser = useSelector((s: RootState) => s.session.currentUser);

  const [suTitle, setSuTitle] = useState("");
  const [suDesc, setSuDesc] = useState("");
  const [suWhen, setSuWhen] = useState(() => localDatetimeValue(new Date()));

  const [maintEnd, setMaintEnd] = useState(() => localDatetimeValue(new Date()));

  const [nKind, setNKind] = useState<"info" | "warning">("info");
  const [nTitle, setNTitle] = useState("");
  const [nDesc, setNDesc] = useState("");

  const [adminNotices, setAdminNotices] = useState<NoticePayload[]>([]);
  const [suHistory, setSuHistory] = useState<ScheduledUpdatePayload[]>([]);
  const [busy, setBusy] = useState(false);

  const simulationRoleItem = useMemo(() => {
    const id = reduxUser?.id ?? "sim";
    let flatSource: string[] = [];
    if (
      roleOverride?.viewKind === "custom" &&
      roleOverride.customFlatNames?.length
    ) {
      flatSource = roleOverride.customFlatNames;
    } else if (reduxUser?.roles?.length) {
      flatSource = sessionRoleRowsToActiveFlatNames(
        reduxUser.roles as { name: string; can: string[] }[]
      );
    }
    return {
      id,
      approved: true,
      roles: flatSource.map((name) => ({ name })),
    };
  }, [
    reduxUser?.id,
    reduxUser?.roles,
    roleOverride?.viewKind,
    roleOverride?.customFlatNames,
  ]);

  useEffect(() => {
    if (!dlgRoleView) return;
    setDraftRolePreset(roleOverride?.activePreset ?? "off");
    setRoleViewTab(
      roleOverride?.viewKind === "custom" ? "custom" : "presets"
    );
    setCustomPanelKey((k) => k + 1);
  }, [dlgRoleView, roleOverride?.viewKind, roleOverride?.activePreset]);

  const activeSu = state?.scheduled_update;

  const loadAdminLists = useCallback(async () => {
    try {
      const [ns, hist] = await Promise.all([
        fetchPlatformNoticesAdmin(),
        fetchScheduledUpdatesHistory(),
      ]);
      if (ns.success) setAdminNotices(ns.data);
      if (hist.success) setSuHistory(hist.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (dlgManageNotices) loadAdminLists();
  }, [dlgManageNotices, loadAdminLists]);

  const canStartMaintenance =
    activeSu?.phase === "announced" && activeSu.id != null;
  const canConfirm =
    activeSu &&
    (activeSu.phase === "in_progress" || activeSu.phase === "announced") &&
    activeSu.id != null;

  const submitSchedule = async () => {
    if (!suTitle.trim() || !suWhen) {
      toast.error(t("platformBroadcast.dev.validationTitleTime"));
      return;
    }
    const iso = new Date(suWhen).toISOString();
    if (Number.isNaN(new Date(iso).getTime())) {
      toast.error(t("platformBroadcast.dev.invalidTime"));
      return;
    }
    setBusy(true);
    try {
      const res = await createScheduledUpdate({
        title: suTitle.trim(),
        description: suDesc.trim() || undefined,
        scheduled_at: iso,
      });
      if (res.success) {
        toast.success(t("platformBroadcast.dev.saved"));
        setDlgSchedule(false);
        setSuTitle("");
        setSuDesc("");
        await refresh();
      } else {
        toast.error((res as { errors?: string[] }).errors?.join(", ") || "Error");
      }
    } catch (e: unknown) {
      toast.error(t("platformBroadcast.dev.requestFailed"));
    } finally {
      setBusy(false);
    }
  };

  const submitMaintain = async () => {
    if (!activeSu?.id || !maintEnd) return;
    const iso = new Date(maintEnd).toISOString();
    setBusy(true);
    try {
      const res = await startMaintenance(activeSu.id, iso);
      if (res.success) {
        toast.success(t("platformBroadcast.dev.maintenanceStarted"));
        setDlgMaintain(false);
        await refresh();
      } else {
        toast.error((res as { errors?: string[] }).errors?.join(", ") || "Error");
      }
    } catch {
      toast.error(t("platformBroadcast.dev.requestFailed"));
    } finally {
      setBusy(false);
    }
  };

  const submitConfirm = async (): Promise<void> => {
    if (!activeSu?.id) return;
    if (
      !window.confirm(
        String(t("platformBroadcast.dev.confirmCompletePrompt"))
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const res = await confirmUpdateComplete(activeSu.id);
      if (res.success) {
        toast.success(t("platformBroadcast.dev.confirmed"));
        await refresh();
      } else {
        toast.error((res as { errors?: string[] }).errors?.join(", ") || "Error");
      }
    } catch {
      toast.error(t("platformBroadcast.dev.requestFailed"));
    } finally {
      setBusy(false);
    }
  };

  const submitNotice = async () => {
    if (!nTitle.trim()) {
      toast.error(t("platformBroadcast.dev.validationTitle"));
      return;
    }
    setBusy(true);
    try {
      const res = await createPlatformNotice({
        kind: nKind,
        title: nTitle.trim(),
        description: nDesc.trim() || undefined,
      });
      if (res.success) {
        toast.success(t("platformBroadcast.dev.noticeCreated"));
        setDlgNotice(false);
        setNTitle("");
        setNDesc("");
        await refresh();
      } else {
        toast.error((res as { errors?: string[] }).errors?.join(", ") || "Error");
      }
    } catch {
      toast.error(t("platformBroadcast.dev.requestFailed"));
    } finally {
      setBusy(false);
    }
  };

  const hideNotice = async (id: number) => {
    setBusy(true);
    try {
      const res = await updatePlatformNotice(id, true);
      if (res.success) {
        toast.success(t("platformBroadcast.dev.noticeHidden"));
        await loadAdminLists();
        await refresh();
      }
    } catch {
      toast.error(t("platformBroadcast.dev.requestFailed"));
    } finally {
      setBusy(false);
    }
  };

  const unhideNotice = async (id: number) => {
    setBusy(true);
    try {
      const res = await updatePlatformNotice(id, false);
      if (res.success) {
        toast.success(t("platformBroadcast.dev.noticeShown"));
        await loadAdminLists();
        await refresh();
      }
    } catch {
      toast.error(t("platformBroadcast.dev.requestFailed"));
    } finally {
      setBusy(false);
    }
  };

  const statusLine = useMemo(() => {
    if (!activeSu) return t("platformBroadcast.dev.noActiveUpdate");
    return t("platformBroadcast.dev.statusLine", {
      phase: activeSu.phase,
      title: activeSu.title,
    });
  }, [activeSu, t]);

  if (!broadcast) return null;

  return (
    <>
      <Menu as="div" className="fixed bottom-5 right-5 z-[600]">
        <Menu.Button
          type="button"
          className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-800 text-white shadow-lg ring-2 ring-white/20 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-400"
          aria-label={String(t("platformBroadcast.dev.menuAria"))}
        >
          <WrenchScrewdriverIcon className="h-6 w-6" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute bottom-14 right-0 z-[610] w-72 origin-bottom-right rounded-md bg-white py-1 shadow-xl ring-1 ring-black/10 focus:outline-none">
            <div className="border-b border-gray-100 px-3 py-2 text-xs text-gray-600">
              {statusLine}
            </div>
            {roleOverride?.isOverrideActive ? (
              <div className="border-b border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                <div className="font-medium">
                  {t("platformBroadcast.dev.roleViewActiveBanner")}
                </div>
                <button
                  type="button"
                  className="mt-1 text-left font-medium text-amber-900 underline decoration-amber-700/60 hover:text-amber-800"
                  onClick={() => {
                    roleOverride.clearOverride();
                    toast.success(
                      String(t("platformBroadcast.dev.roleViewToastReset"))
                    );
                  }}
                >
                  {t("platformBroadcast.dev.roleViewResetNow")}
                </button>
              </div>
            ) : null}
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  className={TWclassNames(
                    active ? "bg-gray-100" : "",
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800"
                  )}
                  onClick={() => setDlgSchedule(true)}
                >
                  <PlusCircleIcon className="h-5 w-5 text-gray-500" />
                  {t("platformBroadcast.dev.scheduleUpdate")}
                </button>
              )}
            </Menu.Item>
            <Menu.Item disabled={!canStartMaintenance}>
              {({ active, disabled }) => (
                <button
                  type="button"
                  disabled={disabled}
                  className={TWclassNames(
                    active && !disabled ? "bg-gray-100" : "",
                    disabled ? "cursor-not-allowed opacity-50" : "",
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800"
                  )}
                  onClick={() => setDlgMaintain(true)}
                >
                  <BoltIcon className="h-5 w-5 text-amber-600" />
                  {t("platformBroadcast.dev.startMaintenance")}
                </button>
              )}
            </Menu.Item>
            <Menu.Item disabled={!canConfirm}>
              {({ active, disabled }) => (
                <button
                  type="button"
                  disabled={disabled}
                  className={TWclassNames(
                    active && !disabled ? "bg-gray-100" : "",
                    disabled ? "cursor-not-allowed opacity-50" : "",
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800"
                  )}
                  onClick={submitConfirm}
                >
                  <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
                  {t("platformBroadcast.dev.confirmComplete")}
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  className={TWclassNames(
                    active ? "bg-gray-100" : "",
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800"
                  )}
                  onClick={() => setDlgNotice(true)}
                >
                  <MegaphoneIcon className="h-5 w-5 text-sky-600" />
                  {t("platformBroadcast.dev.addNotice")}
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  className={TWclassNames(
                    active ? "bg-gray-100" : "",
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800"
                  )}
                  onClick={() => setDlgManageNotices(true)}
                >
                  <EyeSlashIcon className="h-5 w-5 text-gray-500" />
                  {t("platformBroadcast.dev.manageNotices")}
                </button>
              )}
            </Menu.Item>
            {roleOverride ? (
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    className={TWclassNames(
                      active ? "bg-gray-100" : "",
                      "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800"
                    )}
                    onClick={() => {
                      setDraftRolePreset(roleOverride.activePreset ?? "off");
                      setDlgRoleView(true);
                    }}
                  >
                    <IdentificationIcon className="h-5 w-5 text-violet-600" />
                    {t("platformBroadcast.dev.roleViewMenu")}
                  </button>
                )}
              </Menu.Item>
            ) : null}
          </Menu.Items>
        </Transition>
      </Menu>

      <Dialog
        open={dlgSchedule}
        onClose={() => !busy && setDlgSchedule(false)}
        className="relative z-[650]"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {t("platformBroadcast.dev.scheduleUpdate")}
            </Dialog.Title>
            <div className="mt-3 space-y-3">
              <label className="block text-sm text-gray-700">
                {t("platformBroadcast.dev.fieldTitle")}
                <input
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                  value={suTitle}
                  onChange={(e) => setSuTitle(e.target.value)}
                />
              </label>
              <label className="block text-sm text-gray-700">
                {t("platformBroadcast.dev.fieldDescription")}
                <textarea
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                  rows={3}
                  value={suDesc}
                  onChange={(e) => setSuDesc(e.target.value)}
                />
              </label>
              <label className="block text-sm text-gray-700">
                {t("platformBroadcast.dev.fieldScheduledAt")}
                <input
                  type="datetime-local"
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                  value={suWhen}
                  onChange={(e) => setSuWhen(e.target.value)}
                />
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setDlgSchedule(false)}
                disabled={busy}
              >
                {t("platformBroadcast.dev.cancel")}
              </button>
              <button
                type="button"
                className="rounded bg-slate-800 px-3 py-1.5 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
                onClick={submitSchedule}
                disabled={busy}
              >
                {t("platformBroadcast.dev.save")}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog
        open={dlgMaintain}
        onClose={() => !busy && setDlgMaintain(false)}
        className="relative z-[650]"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {t("platformBroadcast.dev.startMaintenance")}
            </Dialog.Title>
            <p className="mt-2 text-sm text-gray-600">
              {t("platformBroadcast.dev.maintenanceHint")}
            </p>
            <label className="mt-3 block text-sm text-gray-700">
              {t("platformBroadcast.dev.fieldExpectedEnd")}
              <input
                type="datetime-local"
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                value={maintEnd}
                onChange={(e) => setMaintEnd(e.target.value)}
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setDlgMaintain(false)}
                disabled={busy}
              >
                {t("platformBroadcast.dev.cancel")}
              </button>
              <button
                type="button"
                className="rounded bg-amber-600 px-3 py-1.5 text-sm text-white hover:bg-amber-500 disabled:opacity-50"
                onClick={submitMaintain}
                disabled={busy}
              >
                {t("platformBroadcast.dev.start")}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog
        open={dlgNotice}
        onClose={() => !busy && setDlgNotice(false)}
        className="relative z-[650]"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {t("platformBroadcast.dev.addNotice")}
            </Dialog.Title>
            <div className="mt-3 space-y-3">
              <label className="block text-sm text-gray-700">
                {t("platformBroadcast.dev.fieldKind")}
                <select
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                  value={nKind}
                  onChange={(e) =>
                    setNKind(e.target.value === "warning" ? "warning" : "info")
                  }
                >
                  <option value="info">{t("platformBroadcast.dev.kindInfo")}</option>
                  <option value="warning">
                    {t("platformBroadcast.dev.kindWarning")}
                  </option>
                </select>
              </label>
              <label className="block text-sm text-gray-700">
                {t("platformBroadcast.dev.fieldTitle")}
                <input
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                  value={nTitle}
                  onChange={(e) => setNTitle(e.target.value)}
                />
              </label>
              <label className="block text-sm text-gray-700">
                {t("platformBroadcast.dev.fieldDescription")}
                <textarea
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                  rows={3}
                  value={nDesc}
                  onChange={(e) => setNDesc(e.target.value)}
                />
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setDlgNotice(false)}
                disabled={busy}
              >
                {t("platformBroadcast.dev.cancel")}
              </button>
              <button
                type="button"
                className="rounded bg-sky-700 px-3 py-1.5 text-sm text-white hover:bg-sky-600 disabled:opacity-50"
                onClick={submitNotice}
                disabled={busy}
              >
                {t("platformBroadcast.dev.save")}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog
        open={dlgManageNotices}
        onClose={() => !busy && setDlgManageNotices(false)}
        className="relative z-[650]"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-100 p-4">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {t("platformBroadcast.dev.manageNotices")}
              </Dialog.Title>
              <p className="mt-1 text-xs text-gray-500">
                {t("platformBroadcast.dev.historyHint")}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <p className="mb-2 text-xs font-semibold uppercase text-gray-500">
                {t("platformBroadcast.dev.recentUpdates")}
              </p>
              <ul className="mb-4 space-y-1 text-xs text-gray-700">
                {suHistory.slice(0, 8).map((r) => (
                  <li key={r.id} className="rounded border border-gray-100 px-2 py-1">
                    <span className="font-mono text-[10px] text-gray-500">
                      #{r.id}
                    </span>{" "}
                    {r.title}{" "}
                    <span className="text-gray-400">({r.phase})</span>
                  </li>
                ))}
              </ul>
              <p className="mb-2 text-xs font-semibold uppercase text-gray-500">
                {t("platformBroadcast.dev.allNotices")}
              </p>
              <ul className="space-y-2">
                {adminNotices.map((n) => (
                  <li
                    key={n.id}
                    className="flex items-start justify-between gap-2 rounded border border-gray-100 p-2 text-sm"
                  >
                    <div className="min-w-0">
                      <div className="font-medium">{n.title}</div>
                      <div className="text-xs text-gray-500">
                        {n.kind}
                        {n.hidden_at ? ` · ${t("platformBroadcast.dev.hidden")}` : ""}
                      </div>
                    </div>
                    {n.hidden_at ? (
                      <button
                        type="button"
                        className="shrink-0 text-xs text-sky-700 hover:underline"
                        onClick={() => unhideNotice(n.id)}
                        disabled={busy}
                      >
                        {t("platformBroadcast.dev.show")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="shrink-0 text-xs text-red-600 hover:underline"
                        onClick={() => hideNotice(n.id)}
                        disabled={busy}
                      >
                        {t("platformBroadcast.dev.hide")}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-gray-100 p-3 text-right">
              <button
                type="button"
                className="rounded px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setDlgManageNotices(false)}
              >
                {t("platformBroadcast.dev.close")}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog
        open={dlgRoleView}
        onClose={() => setDlgRoleView(false)}
        className="relative z-[650]"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-100 p-4">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {t("platformBroadcast.dev.roleViewTitle")}
              </Dialog.Title>
              <p className="mt-2 text-sm text-gray-600">
                {t("platformBroadcast.dev.roleViewHint")}
              </p>
              <div
                className="mt-4 flex gap-1 rounded-lg bg-gray-100 p-1"
                role="tablist"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={roleViewTab === "presets"}
                  className={TWclassNames(
                    roleViewTab === "presets"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900",
                    "flex-1 rounded-md px-3 py-2 text-sm font-medium"
                  )}
                  onClick={() => setRoleViewTab("presets")}
                >
                  {t("platformBroadcast.dev.roleViewTabPresets")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={roleViewTab === "custom"}
                  className={TWclassNames(
                    roleViewTab === "custom"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900",
                    "flex-1 rounded-md px-3 py-2 text-sm font-medium"
                  )}
                  onClick={() => setRoleViewTab("custom")}
                >
                  {t("platformBroadcast.dev.roleViewTabCustom")}
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {roleViewTab === "presets" ? (
                <fieldset className="space-y-2">
                  <legend className="sr-only">
                    {t("platformBroadcast.dev.roleViewTabPresets")}
                  </legend>
                  {ROLE_VIEW_PRESET_ORDER.map((id) => (
                    <label
                      key={id}
                      className="flex cursor-pointer items-start gap-2 rounded-md border border-gray-100 p-2 text-sm hover:bg-gray-50"
                    >
                      <input
                        type="radio"
                        name="role-view-preset"
                        className="mt-0.5"
                        checked={draftRolePreset === id}
                        onChange={() => setDraftRolePreset(id)}
                      />
                      <span>
                        <span className="font-medium text-gray-900">
                          {t(`platformBroadcast.dev.roleViewPreset.${id}.label`)}
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-500">
                          {t(
                            `platformBroadcast.dev.roleViewPreset.${id}.description`
                          )}
                        </span>
                      </span>
                    </label>
                  ))}
                </fieldset>
              ) : (
                <div>
                  <p className="mb-3 text-sm text-gray-600">
                    {t("platformBroadcast.dev.roleViewHintCustom")}
                  </p>
                  <UserRoles
                    key={customPanelKey}
                    simulationMode
                    simulationItemKey={customPanelKey}
                    item={simulationRoleItem}
                    onRefresh={() => {}}
                    onSimulationApply={(activeNames: string[]) => {
                      roleOverride?.applyCustomFlatNames(activeNames);
                      setDlgRoleView(false);
                      toast.success(
                        String(t("platformBroadcast.dev.roleViewToastApplied"))
                      );
                    }}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 p-3">
              {roleOverride?.isOverrideActive ? (
                <button
                  type="button"
                  className="mr-auto rounded px-3 py-1.5 text-sm text-amber-900 hover:bg-amber-50"
                  onClick={() => {
                    roleOverride.clearOverride();
                    setDraftRolePreset("off");
                    setDlgRoleView(false);
                    toast.success(
                      String(t("platformBroadcast.dev.roleViewToastReset"))
                    );
                  }}
                >
                  {t("platformBroadcast.dev.roleViewReset")}
                </button>
              ) : null}
              <button
                type="button"
                className="rounded px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setDlgRoleView(false)}
              >
                {t("platformBroadcast.dev.cancel")}
              </button>
              {roleViewTab === "presets" ? (
                <button
                  type="button"
                  className="rounded bg-violet-700 px-3 py-1.5 text-sm text-white hover:bg-violet-600"
                  onClick={() => {
                    roleOverride?.setPreset(draftRolePreset);
                    setDlgRoleView(false);
                    toast.success(
                      String(t("platformBroadcast.dev.roleViewToastApplied"))
                    );
                  }}
                >
                  {t("platformBroadcast.dev.roleViewApply")}
                </button>
              ) : null}
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}
